/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Typography } from "@mui/material";
import { MapArea, DetailArea, DialogContainer } from "styles/TaskRegister";
import { Map } from "../Map/Map";
import {
  HighlightEdgePolygon,
  HighlightNodePolygon,
  NodeMapOverlay,
} from "components/Map/NodeMapOverlay";
import { ResetMapHeading } from "components/Map/ResetMapHeading";
import { ResetMapPosition } from "components/Map/ResetMapPosition";
import { ConnectingDialog } from "components/Common/ConnectingDialog";
import {
  ButtonContext,
  TaskControlPanel,
} from "components/Tasks/TaskControlPanel";
import { useUserContext } from "context/UserContext";
import { useMapDataContext } from "context/MapDataProvider";
import {
  NEW_NODES_TASK_REGISTER_PROCESSES,
  NO_TASK_LIST_PROCESSES,
  PHASE_SELECT_JOB,
  PHASE_SELECT_TASK,
  PHASE_TASK_CONFIRM,
  PHASE_TASK_SETTING,
} from "./consts";
import { MultipleChargeSelectJobPanel } from "./MultipleChargeSelectJobPanel";
import {
  MultipleChargeJobsetInputItem,
  MultipleChargeSettingPanel,
} from "./MultipleChargeSettingPanel";
import {
  TaskRegisterDataProvider,
  useTaskRegisterDataContext,
} from "context/TaskRegisterDataProvider";
import { MultipleChargeConfirmPanel } from "./MultipleChargeConfirmPanel";
import {
  MultipleChargeTasksItem,
  TaskItem,
} from "components/TaskRegisterMultipleCharge/hooks/GetMultipleChargeTasksHook";
import { SelectTaskPanel } from "components/Tasks/SelectTaskPanel";
import { SpinnerBack } from "components/Common/SpinnerBack";
import {
  JobIdAndRequestedEndDatetimesInfo,
  JobIdInfo,
  RegisterTaskDataMultipleCharge,
  SelectTaskListMultipleCharge,
  UpdateTaskDataMultipleCharge,
} from "client";
import {
  MultipleChargeTaskHistoryItem,
  useTaskHistory,
} from "./hooks/GetMultipleChargeTaskHistoryHook";
import { JobInputItem } from "./JobInputCard";
import {
  createRegisterJobInfos,
  getInitialJobInputItems,
  getInitialJobsetInputItem,
  validateJobInputs,
  validateJobsetInput,
  validateAndEditJobset,
  transformSelectTaskJobInfosToInputJobInfos,
} from "./utils";
import { useJobsetInputPhase } from "./hooks/JobsetInputPhaseHook";
import {
  ConfirmDialog,
  ConfirmDialogProps,
} from "components/Common/ConfirmDialog";
import { useRegisterMultipleChargeTask } from "./hooks/RegisterMultipleChargeTaskHook";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { useUpdateMultipleChargeTasks } from "./hooks/UpdateMultipleChargeTaskHook";
import { useDeleteMultipleChargeTasks } from "./hooks/DeleteMultipleChargeTasksHook";
import { useSubmit } from "./hooks/SubmitHook";
import { AwvMapOverlay } from "components/Map/AwvMapOverlay";
import { useAwvDataContext } from "context/AwvDataProvider";
import { OfflineAwvMapOverlay } from "components/Map/OfflineAwvMapOverlay";
import { ChargerMapOverlay } from "components/Map/ChargerMapOverlay";
import {
  SystemErrorDialog,
  SystemErrorDialogProps,
} from "components/Common/SystemErrorDialog";
import { useRevertMultipleChargeTasks } from "./hooks/RevertMultipleChargeTasksHook";
import { useTranslation } from "react-i18next";
import { useTaskRegisterErrorMessage } from "hooks/TaskRegisterErrorMessageHook";
import { getErrorResponse } from "utils/Api";
import { Const } from "const";

interface TaskRegisterMultipleChargeDialogProps {
  open: boolean;
  jobsetId?: string;
  onClose: () => void;
}

interface SidePanelContext {
  panel: ReactNode;
  backButton: ButtonContext;
  forwardButton: ButtonContext;
  InterruptTaskControlButton: ButtonContext;
}

export const TaskRegisterMultipleChargeDialog: FC<
  TaskRegisterMultipleChargeDialogProps
> = (props) => {
  return (
    <TaskRegisterDataProvider>
      <TaskRegisterMultipleChargeDialogController {...props} />
    </TaskRegisterDataProvider>
  );
};

const TaskRegisterMultipleChargeDialogController: FC<
  TaskRegisterMultipleChargeDialogProps
> = ({ open, jobsetId, onClose }) => {
  const jobsetIdRef = useRef(jobsetId);
  const mode = jobsetIdRef.current === undefined ? "register" : "edit";
  const { t } = useTranslation();
  // States
  //
  const [taskHistories, setTaskHistories] =
    useState<MultipleChargeTaskHistoryItem[]>();
  const [showApiError, setShowApiError] = useState<SystemErrorDialogProps>();
  const [isTilesLoaded, setIsTilesLoaded] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentPanel, setCurrentPanel] = useState<SidePanelContext>();
  const [loading, setLoading] = useState(false);
  const [canForward, setCanForward] = useState(false);
  const [highlightNodeIds, setHighlightNodeIds] = useState<number[]>([]);
  const [confirmContents, setConfirmContents] = useState<ConfirmDialogProps>();
  const [errMsg, setErrMsg] = useState<string>();
  const [registeredJobset, setRegisteredJobset] =
    useState<MultipleChargeTasksItem>();
  const [selectedAwvs, setSelectedAwvs] = useState<string[]>([]);
  const [selectedChargers, setSelectedChargers] = useState<string[]>([]);
  const [highlightTasks, setHighlightTasks] = useState<TaskItem[]>([]);

  // Contexts
  //
  const user = useUserContext();
  const { mapData } = useMapDataContext();
  const mapDataRef = useRef(mapData);
  const mapProps = useMemo(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: user.jobsite?.latitude ?? 0,
        lng: user.jobsite?.longitude ?? 0,
      },
      zoom: user.jobsite?.zoom ?? 11,
      onTilesLoaded: () => {
        setIsTilesLoaded(true);
      },
    };
  }, [user.jobsite]);
  const { ready, errors, chargers } = useTaskRegisterDataContext();

  // AWVs
  const { awvData } = useAwvDataContext();
  const awvsRef = useRef(awvData);

  // origin jobset
  const originRef = useRef<SelectTaskListMultipleCharge>();
  const inputJobs = useRef<JobInputItem[]>(getInitialJobInputItems([], mode));
  const inputJobSet = useRef<MultipleChargeJobsetInputItem>({
    frameTypeAndNumbers: [],
    jobsetName: "",
    startNodeId: -1,
    endNodeId: -1,
    resetJobRequestedEndDatetime: false,
    chargerIds: [],
    chargeOutsideDelivery: true,
    startDate: new Date(),
    endDate: new Date(),
    memo: "",
    moveToStart: false,
  });

  const onCanceledRef = useRef<() => void>();

  // Custom hooks
  //

  // Retrieve task history
  const {
    loading: taskHistoryLoading,
    value: taskHistoryValue,
    error: taskHistoryError,
    setTaskHistoryRequest,
  } = useTaskHistory();
  const taskHistorySelector = useMemo(
    () => taskHistories?.map((item) => item.selectorItem) ?? [],
    [taskHistories]
  );

  // Transision controll
  const { setPhases, taskControlState, goBack, goNext, goNew, currentPhase } =
    useJobsetInputPhase([]);

  // Register jobset
  const {
    loading: registering,
    value: registerResult,
    error: registerError,
    setRequest: register,
  } = useRegisterMultipleChargeTask();

  // Update jobset
  const {
    loading: updating,
    value: updateResult,
    error: updateError,
    setUpdateRequest: update,
  } = useUpdateMultipleChargeTasks();

  // Delete jobset
  const {
    loading: deleting,
    error: deleteError,
    setDeleteJobsetId,
  } = useDeleteMultipleChargeTasks();

  // Revert jobset
  const {
    loading: reverting,
    value: revertResult,
    error: revertError,
    setRevertRequest: revert,
  } = useRevertMultipleChargeTasks();

  // Submit
  const {
    loading: approving,
    error: approveError,
    setApproveRequest,
    approveCompleted,
  } = useSubmit();

  const extendedMsg = useTaskRegisterErrorMessage();

  // Refs
  //

  // Input data
  const jobsetRegisterRequestRef = useRef<RegisterTaskDataMultipleCharge>({
    frameTypeAndNumbers: [],
    mapId: mapData.mapId,
    mapVer: mapData.mapVer,
    jobSetName: "",
    fromNodeId: -1,
    toNodeId: -1,
    resetJobRequestedEndDatetime: false,
    jobInfos: [],
    chargerIds: [],
    chargeOutsideDelivery: true,
    startDatetime: new Date(),
    endDatetime: new Date(),
    memo: "",
    moveToStart: false,
  });

  const jobsetUpdateRequestRef = useRef<UpdateTaskDataMultipleCharge>({
    jobSetId: "",
    jobSetName: "",
    mapId: mapData.mapId,
    mapVer: mapData.mapVer,
    fromNodeId: -1,
    toNodeId: -1,
    frameTypeAndNumbers: [],
    addJobInfos: [],
    updateJobInfos: [],
    deleteJobInfos: [],
    startDatetime: new Date(),
    endDatetime: new Date(),
    resetJobRequestedEndDatetime: true,
    memo: "",
    chargerIds: [],
    chargeOutsideDelivery: true,
    versionId: "",
  });

  const addJobInfosEditRef = useRef<JobInputItem[]>([]);

  const updateJobInfosEditRef = useRef<
    Array<JobIdAndRequestedEndDatetimesInfo>
  >([]);

  const deleteJobInfosEditRef = useRef<Array<JobIdInfo>>([]);

  // Callbacks
  //

  const fillInputFromExistingJobset = useCallback(
    (existingJobset: SelectTaskListMultipleCharge) => {
      jobsetRegisterRequestRef.current.frameTypeAndNumbers =
        existingJobset.frameTypeAndNumbers;
      jobsetRegisterRequestRef.current.jobSetName = existingJobset.jobSetName;
      jobsetRegisterRequestRef.current.fromNodeId = existingJobset.startNodeId;
      jobsetRegisterRequestRef.current.toNodeId = existingJobset.endNodeId;
      jobsetRegisterRequestRef.current.resetJobRequestedEndDatetime =
        existingJobset.resetJobRequestedEndDatetime;
      jobsetRegisterRequestRef.current.jobInfos =
        transformSelectTaskJobInfosToInputJobInfos(existingJobset.jobInfos);
      jobsetRegisterRequestRef.current.chargerIds = existingJobset.chargerIds;
      jobsetRegisterRequestRef.current.chargeOutsideDelivery =
        existingJobset.chargeOutsideDelivery;
      jobsetRegisterRequestRef.current.startDatetime =
        existingJobset.jobsetStartDatetime;
      jobsetRegisterRequestRef.current.endDatetime =
        existingJobset.jobsetEndDatetime;
      jobsetRegisterRequestRef.current.memo = existingJobset.memo;
      jobsetRegisterRequestRef.current.moveToStart = existingJobset.moveToStart;

      inputJobSet.current = getInitialJobsetInputItem(existingJobset);
      inputJobs.current = getInitialJobInputItems(
        existingJobset.jobInfos,
        mode
      );
    },
    [mode]
  );

  /**
   * Callback for task history selected
   *
   * @type {(jobsetId: string):void}
   */
  const handleTaskHistorySelect = useCallback(
    (jobsetId: string) => {
      // Initialize input data by task history
      const selectedHistory = taskHistories?.find(
        (history) => history.selectorItem.jobsetId === jobsetId
      );
      if (selectedHistory !== undefined) {
        fillInputFromExistingJobset(selectedHistory.jobsetInfo);
        console.dir(jobsetRegisterRequestRef.current);

        // Activate next button
        setCanForward(true);
      }
    },
    [fillInputFromExistingJobset, taskHistories]
  );

  /**
   * Callback for highlight loading/unloading node on map
   *
   * @param {JobInputItem} job Target job
   */
  const highlightJobNodes = useCallback((job?: JobInputItem) => {
    const nodeIds = [];
    if (job?.loadingNodeId !== undefined) {
      nodeIds.push(job.loadingNodeId);
    }
    if (job?.unloadingNodeId !== undefined) {
      nodeIds.push(job.unloadingNodeId);
    }
    setHighlightNodeIds(nodeIds);
  }, []);

  /**
   * Callback for highlight start/end node on map
   *
   * @param {MultipleChargeJobsetInputItem} jobset Target jobset
   */
  const highlightJobsetNodes = useCallback(
    (jobset?: MultipleChargeJobsetInputItem) => {
      const nodeIds = [];
      if (jobset?.startNodeId !== undefined) {
        nodeIds.push(jobset.startNodeId);
      }
      if (jobset?.endNodeId !== undefined) {
        nodeIds.push(jobset.endNodeId);
      }
      setHighlightNodeIds(nodeIds);
    },
    []
  );

  /**
   * Callback for job add/edit/delete (register mode)
   *
   * @param {JobInputItem[]} jobs Job input data
   * @param {number} jobId add/edit/delete job ID (not used)
   */
  const handleJobInputRegister = useCallback(
    (jobs: JobInputItem[], jobId: number, active: boolean) => {
      inputJobs.current = jobs;
      // Validate input
      const validate = validateJobInputs(jobs);

      if (validate) {
        // Set to ragister input data
        jobsetRegisterRequestRef.current.jobInfos =
          createRegisterJobInfos(jobs);

        // Activate next button
        setCanForward(true);
      } else {
        // Deactivate next button
        setCanForward(false);
      }

      if (active) {
        // Update highlighted node
        highlightJobNodes(jobs.find((job) => job.jobId === jobId));
      }
    },
    [highlightJobNodes]
  );

  /**
   * Callback for input jobset item (register mode)
   *
   * @param {MultipleChargeJobsetInputItem} jobset input value
   */
  const handleJobsetInputRegister = useCallback(
    (jobset: MultipleChargeJobsetInputItem) => {
      inputJobSet.current = jobset;
      highlightJobsetNodes(jobset);
      // Validate input
      const validate = validateJobsetInput(jobset);
      if (validate) {
        // Set to ragister input data
        jobsetRegisterRequestRef.current.frameTypeAndNumbers =
          jobset.frameTypeAndNumbers;
        jobsetRegisterRequestRef.current.jobSetName = jobset.jobsetName!;
        jobsetRegisterRequestRef.current.fromNodeId = jobset.startNodeId!;
        jobsetRegisterRequestRef.current.toNodeId = jobset.endNodeId!;
        jobsetRegisterRequestRef.current.resetJobRequestedEndDatetime =
          jobset.resetJobRequestedEndDatetime;
        // jobsetInputRef.current.untreatedJobInfos = jobset.untreatedJobInfos;
        jobsetRegisterRequestRef.current.chargerIds = jobset.chargerIds;
        jobsetRegisterRequestRef.current.chargeOutsideDelivery =
          jobset.chargeOutsideDelivery;
        jobsetRegisterRequestRef.current.startDatetime = jobset.startDate!;
        jobsetRegisterRequestRef.current.endDatetime = jobset.endDate!;
        jobsetRegisterRequestRef.current.memo = jobset.memo!;
        jobsetRegisterRequestRef.current.moveToStart = jobset.moveToStart;

        // Activate next button
        setCanForward(true);
      } else {
        // Deactivate next button
        setCanForward(false);
      }
      setSelectedAwvs([...jobset.frameTypeAndNumbers]);
      setSelectedChargers([...jobset.chargerIds]);
    },
    [highlightJobsetNodes]
  );

  /**
   * Callback for job input card activated
   *
   * @param {JobInputItem} job Activated job
   */
  const handleActiveJobChanged = useCallback(
    (job: JobInputItem) => {
      highlightJobNodes(job);
    },
    [highlightJobNodes]
  );

  /**
   * Callback for AWV tab activated
   *
   * @param {string} frameTypeAndNumber Activated AWV
   */
  const handleActiveAwvTabChanged = useCallback(
    (frameTypeAndNumber: string): void => {
      console.debug(frameTypeAndNumber);
      setSelectedAwvs([frameTypeAndNumber]);
      setHighlightTasks(
        registeredJobset?.tasks.filter(
          (task) => task.frameTypeNumber === frameTypeAndNumber
        ) ?? []
      );
    },
    [registeredJobset?.tasks]
  );

  /**
   * Callback for cancel register/edit
   *
   * @param {() => void} onPositive Callback for OK clicled.
   */
  const confirmCancel = useCallback(
    (onPositive?: () => void) => {
      setConfirmContents({
        open: true,
        message: mode === "register" ? t("AWVUI-MSG10") : t("AWVUI-MSG105"),
        negativeButtonText: t("AWVUI-MSG12"),
        positiveButtonText: t("AWVUI-MSG11"),
        onNegativeButtonClick: () => {
          setConfirmContents(undefined);
        },
        onPositiveButtonClick: () => {
          setConfirmContents(undefined);
          if (onPositive !== undefined) {
            onPositive();
          } else {
            onClose?.();
          }
        },
      });
    },
    [mode, onClose, t]
  );

  /**
   * Callback for next button of setting panel (register mode)
   */
  const handleSettingNextRegister = useCallback(() => {
    console.log("jobsetInputRef.current", jobsetRegisterRequestRef.current);
    register(jobsetRegisterRequestRef.current);
  }, [register]);

  /**
   * Callback for back button of setting panel (register mode)
   */
  const handleConfirmBackRegister = useCallback(() => {
    onCanceledRef.current = () => {
      setDeleteJobsetId(undefined);
      goBack();
    };
    setDeleteJobsetId(registeredJobset?.jobsetId);
  }, [goBack, registeredJobset?.jobsetId, setDeleteJobsetId]);

  /**
   * Callback for submit button
   */
  const handleSubmit = useCallback(() => {
    if (registeredJobset !== undefined) {
      // Approve registered jobset.
      setApproveRequest({
        jobSetId: registeredJobset.jobsetId,
        jobSetApproval: true,
      });
    }
  }, [registeredJobset, setApproveRequest]);

  /**
   * Callback for job add/edit/delete (edit mode)
   *
   * @param {JobInputItem[]} jobs Job input data
   * @param {number} jobId add/edit/delete job ID (not used)
   */
  const handleJobAddEdit = useCallback(
    (jobs: JobInputItem[], jobId: number) => {
      inputJobs.current = jobs;
      // Find job in input jobs to match input jobId.
      const job = jobs.find((job) => job.jobId === jobId);

      const addJobInfo = addJobInfosEditRef.current.find(
        (addJobInfo) => addJobInfo.jobId === jobId
      );
      if (job !== undefined && addJobInfo === undefined) {
        // If there is no input jobId in Ref's addJobInfos.
        addJobInfosEditRef.current.push({
          jobId: job.jobId,
          materialType: job.materialType ?? 1,
          materialQuantity: job.materialQuantity ?? 1,
          loadingNodeId: job.loadingNodeId ?? 0,
          extensionTimeLoad: job.extensionTimeLoad ?? 0,
          loadingUserId: job.loadingUserId ?? "",
          unloadingNodeId: job.unloadingNodeId ?? 0,
          extensionTimeUnload: job.extensionTimeUnload ?? 0,
          unloadingUserId: job.unloadingUserId ?? "",
          requestedEndDatetimeFrom: job.requestedEndDatetimeFrom ?? new Date(),
          requestedEndDatetimeTo: job.requestedEndDatetimeTo ?? new Date(),
          mode: "register",
        });
      }

      setCanForward(false);
    },
    []
  );

  /**
   * Callback for job add/edit/delete (edit mode)
   *
   * @param {JobInputItem[]} jobs Job input data
   * @param {number} jobId add/edit/delete job ID (not used)
   */
  const handleJobUpdateEdit = useCallback(
    (jobs: JobInputItem[], jobId: number) => {
      inputJobs.current = jobs;
      // Find job to match input jobId in input jobs.
      const job = jobs.find((job) => job.jobId === jobId);

      if (job !== undefined) {
        const addJobInfo = addJobInfosEditRef.current.find(
          (addJobInfo) => addJobInfo.jobId === jobId
        );
        if (addJobInfo !== undefined) {
          // If addJobInfosEditRef has addJobInfo to match input jobId.
          addJobInfo.jobId = job.jobId;
          addJobInfo.materialType = job.materialType;
          addJobInfo.materialQuantity = job.materialQuantity;
          addJobInfo.loadingNodeId = job.loadingNodeId;
          addJobInfo.extensionTimeLoad = job.extensionTimeLoad;
          addJobInfo.loadingUserId = job.loadingUserId;
          addJobInfo.unloadingNodeId = job.unloadingNodeId;
          addJobInfo.extensionTimeUnload = job.extensionTimeUnload;
          addJobInfo.unloadingUserId = job.unloadingUserId;
          addJobInfo.requestedEndDatetimeFrom = job.requestedEndDatetimeFrom;
          addJobInfo.requestedEndDatetimeTo = job.requestedEndDatetimeTo;
          addJobInfo.mode = job.mode;
        } else {
          const updateJobInfo = updateJobInfosEditRef.current.find(
            (updateJobInfo) => updateJobInfo.jobId === jobId
          );
          if (updateJobInfo === undefined) {
            // If there is no input jobId in Ref's updateJobInfos.
            updateJobInfosEditRef.current.push({
              jobId: job.jobId,
              requestedEndDatetimes: [
                {
                  endFrom: job.requestedEndDatetimeFrom,
                  endTo: job.requestedEndDatetimeTo,
                },
              ],
            });
          } else {
            // If input jobId has already existed in Ref's updateJobInfos.
            updateJobInfo.requestedEndDatetimes = [
              {
                endFrom: job.requestedEndDatetimeFrom,
                endTo: job.requestedEndDatetimeTo,
              },
            ];
          }
        }
      }

      const validated = validateAndEditJobset(
        jobs,
        addJobInfosEditRef.current,
        updateJobInfosEditRef.current,
        deleteJobInfosEditRef.current,
        jobsetUpdateRequestRef.current
      );
      setCanForward(validated);
    },
    []
  );

  /**
   * Callback for job add/edit/delete (edit mode)
   *
   * @param {JobInputItem[]} jobs Job input data
   * @param {number} jobId add/edit/delete job ID (not used)
   */
  const handleJobDeleteEdit = useCallback(
    (jobs: JobInputItem[], jobId: number) => {
      inputJobs.current = jobs;
      const addJobInfo = addJobInfosEditRef.current.find(
        (addJobInfo) => addJobInfo.jobId === jobId
      );
      if (addJobInfo !== undefined) {
        // delete from addJobInfo
        addJobInfosEditRef.current = addJobInfosEditRef.current.filter(
          (addJobInfo) => addJobInfo.jobId !== jobId
        );
      } else {
        // Set data is planed to delete.
        deleteJobInfosEditRef.current.push({
          jobId: jobId,
        });
      }

      const validated = validateAndEditJobset(
        jobs,
        addJobInfosEditRef.current,
        updateJobInfosEditRef.current,
        deleteJobInfosEditRef.current,
        jobsetUpdateRequestRef.current
      );
      setCanForward(validated);
    },
    []
  );

  /**
   * Callback for next button of setting panel (edit mode)
   */
  const handleSettingNextEdit = useCallback(() => {
    jobsetUpdateRequestRef.current.jobSetId =
      inputJobSet.current.jobsetId ?? "";
    jobsetUpdateRequestRef.current.jobSetName = inputJobSet.current.jobsetName;
    jobsetUpdateRequestRef.current.fromNodeId = inputJobSet.current.startNodeId;
    jobsetUpdateRequestRef.current.toNodeId = inputJobSet.current.endNodeId;
    jobsetUpdateRequestRef.current.memo = inputJobSet.current.memo;
    jobsetUpdateRequestRef.current.frameTypeAndNumbers =
      inputJobSet.current.frameTypeAndNumbers;
    jobsetUpdateRequestRef.current.startDatetime =
      inputJobSet.current.startDate;
    jobsetUpdateRequestRef.current.endDatetime = inputJobSet.current.endDate;
    jobsetUpdateRequestRef.current.resetJobRequestedEndDatetime =
      inputJobSet.current.resetJobRequestedEndDatetime;
    jobsetUpdateRequestRef.current.chargerIds = inputJobSet.current.chargerIds;
    jobsetUpdateRequestRef.current.chargeOutsideDelivery =
      inputJobSet.current.chargeOutsideDelivery;
    jobsetUpdateRequestRef.current.versionId =
      inputJobSet.current.versionId ?? "";

    update(jobsetUpdateRequestRef.current);
  }, [update]);

  /**
   * Send request to revert jobset
   */
  const requestRevert = useCallback(() => {
    if (originRef.current !== undefined) {
      revert({
        origin: originRef.current,
        update: jobsetUpdateRequestRef.current,
      });
    }
  }, [revert]);

  /**
   * Callback for back button of setting panel (register mode)
   */
  const handleConfirmBackEdit = useCallback(() => {
    onCanceledRef.current = goBack;
    requestRevert();
  }, [goBack, requestRevert]);

  /**
   * Callback for aborting register
   */
  const abortConfirmRegister = useCallback(() => {
    onCanceledRef.current = onClose;
    setDeleteJobsetId(registeredJobset?.jobsetId);
  }, [onClose, registeredJobset?.jobsetId, setDeleteJobsetId]);

  /**
   * Callback for aborting edit
   */
  const abortConfirmEdit = useCallback(() => {
    onCanceledRef.current = onClose;
    requestRevert();
  }, [onClose, requestRevert]);

  /**
   * Callback for the action after API error message closed.
   */
  const handleAfterApiError = useCallback(() => {
    setShowApiError(undefined);
    onClose?.();
  }, [onClose]);

  /**
   * Raise API error message.
   * @param {Error} error Raised error
   */
  const raiseApiError = useCallback(
    (error?: Error) => {
      setShowApiError({
        open: true,
        error: error,
        onClose: handleAfterApiError,
      });
    },
    [handleAfterApiError]
  );

  // Side effects
  //

  // Side effect for initialize
  useEffect(() => {
    setTaskHistoryRequest(
      open
        ? {
            mapId: mapDataRef.current.mapId,
            jobSetId: jobsetIdRef.current,
          }
        : undefined
    );
  }, [open, setTaskHistoryRequest]);

  // Side effect for initialize
  useEffect(() => {
    setLoading(!ready || taskHistoryLoading);

    return () => {
      setLoading(false);
    };
  }, [ready, taskHistoryLoading]);

  // Side effect for occuring initialization error
  useEffect(() => {
    if (ready && errors.length > 0) {
      raiseApiError(errors[0]);
    }
  }, [errors, raiseApiError, ready]);

  // Side effect for task history
  useEffect(() => {
    console.debug("useEffect");
    if (!taskHistoryLoading) {
      if (taskHistoryError === undefined) {
        if (
          mode === "edit" &&
          taskHistoryValue !== undefined &&
          taskHistoryValue.length > 0
        ) {
          // Keep result for original jobset
          originRef.current = taskHistoryValue[0].jobsetInfo;
          fillInputFromExistingJobset(taskHistoryValue[0].jobsetInfo);
        }
        setTaskHistories(taskHistoryValue);
      } else {
        raiseApiError(taskHistoryError);
      }
    }
  }, [
    fillInputFromExistingJobset,
    mode,
    raiseApiError,
    taskHistoryError,
    taskHistoryLoading,
    taskHistoryValue,
  ]);

  // Side effect for initiailze screen from existing jobset(s)
  useEffect(() => {
    if (taskHistories !== undefined) {
      if (mode === "register") {
        setPhases(
          taskHistories.length === 0
            ? NO_TASK_LIST_PROCESSES
            : NEW_NODES_TASK_REGISTER_PROCESSES
        );
      } else {
        setPhases(NO_TASK_LIST_PROCESSES);
      }
    }
  }, [mode, setPhases, taskHistories]);

  // Side effect for task register
  useEffect(() => {
    const func = async () => {
      setIsConnecting(registering);
      if (registerResult !== undefined) {
        // Reset register request
        register(undefined);

        // Keep registered result
        setRegisteredJobset(registerResult);

        // Go forward.
        goNext();
      }
      if (registerError !== undefined) {
        console.log("1");
        setErrMsg(await extendedMsg(registerError, mode));
      }
    };
    func();
  }, [
    extendedMsg,
    goNext,
    mode,
    register,
    registerError,
    registerResult,
    registering,
  ]);

  // Side effect for delete task (back to setting panel)
  useEffect(() => {
    const func = async () => {
      setIsConnecting(deleting);
      if (!deleting) {
        if (deleteError === undefined) {
          // Process after deleted.
          onCanceledRef.current?.();
        } else {
          console.log("2");
          setErrMsg(await extendedMsg(deleteError, mode));
        }
      }
    };
    func();
  }, [deleteError, deleting, extendedMsg, mode]);

  // Side effect for update task
  useEffect(() => {
    const func = async () => {
      setIsConnecting(updating);
      if (updateResult !== undefined) {
        // Reset update request
        update(undefined);

        // Keep updated result
        setRegisteredJobset(updateResult);

        // Go forward.
        goNext();
      }
      if (updateError !== undefined) {
        const errorResponse = await getErrorResponse(updateError);
        if (
          errorResponse?.errors?.includes(Const.errorCodes.taskUpdateConflict)
        ) {
          // Show conflict message
          setConfirmContents({
            open: true,
            message: (
              <>
                <Typography variant="subtitle1">{t("AWVUI-MSG155")}</Typography>
                <Typography variant="subtitle1">{t("AWVUI-MSG156")}</Typography>
              </>
            ),
            positiveButtonText: t("AWVUI-MSG11"),
            onPositiveButtonClick: () => {
              setConfirmContents(undefined);
              onClose?.();
            },
          });
        } else {
          // Show general error message
          setErrMsg(await extendedMsg(updateError, mode));
        }
      }
    };
    func();
  }, [
    extendedMsg,
    goNext,
    mode,
    onClose,
    t,
    update,
    updateError,
    updateResult,
    updating,
  ]);

  // Side effect for revert task (back to setting panel)
  useEffect(() => {
    const func = async () => {
      setIsConnecting(reverting);
      if (revertResult !== undefined) {
        // Reset revert request
        revert(undefined);

        // Keep reverted result
        setRegisteredJobset(revertResult);
        inputJobSet.current.versionId = revertResult.versionId;

        // Process after reverted.
        onCanceledRef.current?.();
      }
      if (revertError !== undefined) {
        setErrMsg(await extendedMsg(revertError, mode));
      }
    };
    func();
  }, [extendedMsg, mode, revert, revertError, revertResult, reverting]);

  // Side effect for submit
  useEffect(() => {
    const func = async () => {
      setIsConnecting(approving);
      if (!approving) {
        if (approveError === undefined) {
          if (approveCompleted) {
            // Close this
            onClose();
          }
        } else {
          setErrMsg(await extendedMsg(approveError, mode));
        }
      }
    };
    func();
  }, [approveCompleted, approveError, approving, extendedMsg, mode, onClose]);

  useEffect(() => {
    awvsRef.current = awvData;
  }, [awvData]);

  // Side effect for phase changed
  useLayoutEffect(() => {
    console.debug("useLayoutEffect");
    // Select panel
    switch (currentPhase) {
      case PHASE_SELECT_TASK:
        setHighlightNodeIds([]);
        setSelectedAwvs([]);
        setSelectedChargers([]);
        setCurrentPanel({
          panel: (
            <SelectTaskPanel
              taskList={taskHistorySelector}
              jobsetId={inputJobSet.current.jobsetId}
              onNewClick={() => {
                goNew();
              }}
              onTaskSelected={handleTaskHistorySelect}
            />
          ),
          backButton: {
            text: t("AWVUI-MSG29"),
            action: () => {
              confirmCancel();
            },
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action: () => {
              goNext();
            },
          },
          InterruptTaskControlButton: {
            action: () => {
              confirmCancel();
            },
            text: "",
          },
        });
        break;
      case PHASE_SELECT_JOB:
        setCanForward(validateJobInputs(inputJobs.current));
        setHighlightNodeIds([]);
        setSelectedAwvs([]);
        setSelectedChargers([]);
        setCurrentPanel({
          panel: (
            <MultipleChargeSelectJobPanel
              jobs={inputJobs.current}
              mode={mode}
              onAdd={
                mode === "register" ? handleJobInputRegister : handleJobAddEdit
              }
              onEdit={
                mode === "register"
                  ? handleJobInputRegister
                  : handleJobUpdateEdit
              }
              onDelete={
                mode === "register"
                  ? handleJobInputRegister
                  : handleJobDeleteEdit
              }
              onActiveJobChanged={handleActiveJobChanged}
            />
          ),
          backButton: {
            text:
              taskControlState.process[0] === PHASE_SELECT_JOB
                ? t("AWVUI-MSG29")
                : t("AWVUI-MSG52"),
            action: () => {
              if (taskControlState.process[0] === PHASE_SELECT_JOB) {
                confirmCancel();
              } else {
                goBack();
              }
            },
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action: () => {
              goNext();
            },
          },
          InterruptTaskControlButton: {
            action: () => {
              confirmCancel();
            },
            text: "",
          },
        });
        break;
      case PHASE_TASK_SETTING:
        highlightJobsetNodes(inputJobSet.current);
        // Set default AWV selection
        if (inputJobSet.current.frameTypeAndNumbers?.length === 0) {
          inputJobSet.current.frameTypeAndNumbers =
            awvsRef.current.awvInfos
              ?.map((awv) => awv.frameTypeAndNumber ?? "")
              .filter((i) => i !== "") ?? [];
        }
        setSelectedAwvs([...inputJobSet.current.frameTypeAndNumbers]);

        // Set default chargers selection
        if (inputJobSet.current.chargerIds?.length === 0) {
          inputJobSet.current.chargerIds =
            chargers?.map((charger) => charger.id) ?? [];
        }
        setSelectedChargers(
          inputJobSet.current.chargerIds !== undefined
            ? [...inputJobSet.current.chargerIds]
            : []
        );
        setHighlightTasks([]);
        setCanForward(validateJobsetInput(inputJobSet.current));
        setCurrentPanel({
          panel: (
            <MultipleChargeSettingPanel
              jobset={inputJobSet.current}
              mode={mode}
              onEdit={handleJobsetInputRegister}
            />
          ),
          backButton: {
            text: t("AWVUI-MSG52"),
            action: () => {
              goBack();
            },
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action:
              mode === "register"
                ? handleSettingNextRegister
                : handleSettingNextEdit,
          },
          InterruptTaskControlButton: {
            action: () => {
              confirmCancel();
            },
            text: "",
          },
        });
        break;
      case PHASE_TASK_CONFIRM:
        // Clear selected.
        setHighlightNodeIds([]);
        setSelectedChargers([]);

        // Highlight AWV and route
        const headAwv = awvsRef.current.awvInfos?.[0].frameTypeAndNumber;
        if (headAwv !== undefined) {
          setSelectedAwvs([headAwv]);
          const headAwvTasks = registeredJobset?.tasks.filter(
            (task) => task.frameTypeNumber === headAwv
          );
          setHighlightTasks(headAwvTasks ?? []);
        }
        setCurrentPanel({
          panel: (
            <MultipleChargeConfirmPanel
              tasks={registeredJobset!}
              onAwvSelected={handleActiveAwvTabChanged}
            />
          ),
          backButton: {
            text: t("AWVUI-MSG52"),
            action:
              mode === "register"
                ? handleConfirmBackRegister
                : handleConfirmBackEdit,
          },
          forwardButton: {
            text: t("AWVUI-MSG60"),
            action: handleSubmit,
          },
          InterruptTaskControlButton: {
            action: () => {
              confirmCancel(
                mode === "register" ? abortConfirmRegister : abortConfirmEdit
              );
            },
            text: "",
          },
        });
        break;
      default:
        break;
    }
  }, [
    abortConfirmEdit,
    abortConfirmRegister,
    chargers,
    confirmCancel,
    currentPhase,
    goBack,
    goNew,
    goNext,
    handleActiveAwvTabChanged,
    handleActiveJobChanged,
    handleConfirmBackEdit,
    handleConfirmBackRegister,
    handleJobAddEdit,
    handleJobDeleteEdit,
    handleJobInputRegister,
    handleJobUpdateEdit,
    handleJobsetInputRegister,
    handleSettingNextEdit,
    handleSettingNextRegister,
    handleSubmit,
    handleTaskHistorySelect,
    highlightJobsetNodes,
    mode,
    registeredJobset,
    t,
    taskControlState.process,
    taskHistorySelector,
  ]);

  return (
    <>
      <SpinnerBack open={loading} />
      <Dialog
        maxWidth={false}
        fullWidth
        key={open ? "open" : "closed"}
        open={open}
        PaperProps={{ sx: { height: "100%" } }}
      >
        <DialogContainer sidePanelWidth="450px">
          <MapArea>
            <Map {...mapProps}>
              {isTilesLoaded && (
                <>
                  <ResetMapPosition
                    center={mapProps.center}
                    zoom={mapProps.zoom}
                  />
                  <ResetMapHeading />
                  <OfflineAwvMapOverlay
                    awvInfos={awvData}
                    selected={selectedAwvs}
                  />
                </>
              )}
              <AwvMapOverlay awvInfos={awvData} selected={selectedAwvs} />
              <NodeMapOverlay mapData={mapDataRef.current} />
              <ChargerMapOverlay selected={selectedChargers} />
              <HighlightNodePolygon
                mapData={mapDataRef.current}
                nodeIds={highlightNodeIds}
              />
              {highlightTasks.map((task) => {
                return (
                  <HighlightEdgePolygon
                    key={`highlightTask-${task.taskId}`}
                    mapData={mapDataRef.current}
                    highlightEdges={task.actualRootInfo}
                  />
                );
              })}
            </Map>
          </MapArea>
          <DetailArea>
            {currentPanel && (
              <TaskControlPanel
                state={taskControlState}
                canForward={canForward}
                forwardButtonContext={currentPanel?.forwardButton}
                backButtonContext={currentPanel?.backButton}
                interruptButtonContext={
                  currentPanel?.InterruptTaskControlButton
                }
              >
                {currentPanel?.panel}
              </TaskControlPanel>
            )}
          </DetailArea>
        </DialogContainer>
        <ConnectingDialog open={isConnecting} />
      </Dialog>
      <ConfirmDialog
        open={confirmContents !== undefined}
        title={confirmContents?.title}
        message={confirmContents?.message}
        positiveButtonText={confirmContents?.positiveButtonText ?? ""}
        onPositiveButtonClick={() => {
          confirmContents?.onPositiveButtonClick();
        }}
        negativeButtonText={confirmContents?.negativeButtonText}
        onNegativeButtonClick={confirmContents?.onNegativeButtonClick}
      />
      <ErrorDialog
        open={errMsg !== undefined}
        onPositiveButtonClick={() => {
          setErrMsg(undefined);

          // Close this
          onClose();
        }}
        message={
          <Typography variant="inherit" sx={{ whiteSpace: "pre-wrap" }}>
            {errMsg}
          </Typography>
        }
        positiveButtonText={t("AWVUI-MSG22")}
      />
      {showApiError && (
        <SystemErrorDialog {...showApiError} open={!!showApiError} />
      )}
    </>
  );
};
